import { defineStore } from "pinia";
import axios from "axios";
import Cookie from "js-cookie";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.VUE_APP_BACKEND_API_URL;
const authHeader = () => ({
  Authorization: `Bearer ${Cookie.get("saa_token")}`,
});

export const useDistributionPlanStore = defineStore("distributionPlan", {
  state: () => ({
    // Data antrean pesanan siap kirim
    availableOrders: [],
    summary: {
      total_orders: 0,
      total_so: 0,
      total_sm: 0,
      total_tonase: 0,
    },
    loading: false,
    error: null,
    lastFetched: null,

    // Seleksi dokumen pesanan
    selectedKeys: [],

    // Filter lokal pencarian cepat
    searchQuery: "",
    filterDocType: "ALL", // 'ALL' | 'SO' | 'SM'
    filterDeliveryType: "kirim", // default hanya menampilkan yang kirim
    filterCabang: "", // '' (semua cabang user) atau kode spesifik

    // Modal Form State: Distribution Plan Gabungan
    isModalOpen: false,
    isSubmitting: false,
    modalForm: {
      tanggal: new Date().toISOString().split("T")[0],
      area: "",
      wh_office: "",
      keterangan: "",
      no_dp_gandengan: "",
      ambil: false,
      full_load: false,
      prioritas: false,
      truk_luar: false,
      truk_sna: false,
      perlu_gandengan: false,
      items: [], // [{ key, doc_no, type, ledger, name, sm, so, item_code, item_name, max_qty, qty, item_weight, item_ton }]
    },

    // Riwayat / Daftar Dokumen Displan yang sudah jadi
    distributionPlans: [],
    loadingPlans: false,
    planStartDate: "",
    planEndDate: "",
    planFilterCabang: "",
    planFilterArea: "",
    planSearchQuery: "",
    selectedPlanIds: [],
    recentCreatedPlans: [],
  }),

  getters: {
    /**
     * Memfilter daftar order berdasarkan search query dan chip filter
     */
    filteredOrders: (state) => {
      let list = state.availableOrders || [];

      // Filter Tipe Dokumen
      if (state.filterDocType !== "ALL") {
        list = list.filter(
          (o) => String(o.type).toUpperCase() === state.filterDocType
        );
      }

      // Filter Tipe Pengiriman (kirim / ambil)
      if (state.filterDeliveryType && state.filterDeliveryType !== "ALL") {
        list = list.filter((o) => {
          const fType = String(o.freight_type || "kirim").toLowerCase();
          return fType.includes(state.filterDeliveryType.toLowerCase());
        });
      }

      // Filter Cabang jika dispesifikkan
      if (state.filterCabang) {
        list = list.filter(
          (o) =>
            String(o.cabang).toUpperCase().trim() ===
            state.filterCabang.toUpperCase().trim()
        );
      }

      // Filter Pencarian Teks Bebas
      const q = String(state.searchQuery || "").trim().toLowerCase();
      if (q) {
        list = list.filter((o) => {
          const soNo = String(o.so_no || "").toLowerCase();
          const smNo = String(o.sm_no || "").toLowerCase();
          const docNo = String(o.no || "").toLowerCase();
          const custName = String(o.ledgername || "").toLowerCase();
          const custCode = String(o.code || "").toLowerCase();
          const alamatToko = String(o.alamat_toko || "").toLowerCase();
          const alamatKirim = String(
            o.alamat_pengiriman || o.delivery_address || ""
          ).toLowerCase();
          const deliveryName = String(o.delivery_name || "").toLowerCase();
          const addrCode = String(o.address_code || "").toLowerCase();
          const itemSummary = String(o.items_summary || "").toLowerCase();
          const ket = String(o.keterangan || "").toLowerCase();

          return (
            soNo.includes(q) ||
            smNo.includes(q) ||
            docNo.includes(q) ||
            custName.includes(q) ||
            custCode.includes(q) ||
            alamatToko.includes(q) ||
            alamatKirim.includes(q) ||
            deliveryName.includes(q) ||
            addrCode.includes(q) ||
            itemSummary.includes(q) ||
            ket.includes(q)
          );
        });
      }

      return list;
    },

    /**
     * Mengambil array dokumen order yang sedang dicentang oleh user
     */
    selectedOrders: (state) => {
      if (!state.selectedKeys || state.selectedKeys.length === 0) return [];
      const keySet = new Set(state.selectedKeys);
      return state.availableOrders.filter((o) => keySet.has(o.key));
    },

    /**
     * Menghitung total akumulasi dari order yang sedang dicentang
     */
    selectedMetrics: (state) => {
      const selected = state.availableOrders.filter((o) =>
        state.selectedKeys.includes(o.key)
      );
      let totalQty = 0;
      let totalTonase = 0;

      selected.forEach((o) => {
        totalQty += Number(o.total_qty || 0);
        totalTonase += Number(o.total_tonase || 0);
      });

      return {
        count: selected.length,
        totalQty: Math.round(totalQty * 100) / 100,
        totalTonase: Math.round(totalTonase * 1000) / 1000,
      };
    },

    /**
     * Memeriksa apakah semua baris pada filteredOrders sedang terpilih
     */
    isAllSelected: (state) => {
      const list = state.filteredOrders;
      if (!list || list.length === 0) return false;
      return list.every((o) => state.selectedKeys.includes(o.key));
    },

    /**
     * Memfilter daftar Distribution Plan yang sudah diterbitkan
     */
    filteredDistributionPlans: (state) => {
      let list = state.distributionPlans || [];
      const q = String(state.planSearchQuery || "").trim().toLowerCase();
      if (q) {
        list = list.filter((p) => {
          const noDp = String(p.no_dp || "").toLowerCase();
          const area = String(p.area || p.antrian_area || "").toLowerCase();
          const cabang = String(p.cabang || p.antrian_cabang || "").toLowerCase();
          const ket = String(p.catatan || p.keterangan || "").toLowerCase();
          const noPol = String(p.no_pol || "").toLowerCase();
          const sopir = String(p.sopir || "").toLowerCase();
          const items = p.items || p.detail || [];

          const matchItems = items.some((it) => {
            const custName = String(it.name || "").toLowerCase();
            const custCode = String(it.code || "").toLowerCase();
            const smNo = String(it.no || "").toLowerCase();
            const soNo = String(it.so_no || "").toLowerCase();
            const itemCode = String(it.item_code || "").toLowerCase();
            const itKet = String(it.keterangan || "").toLowerCase();
            const addr = String(it.alamat_pengiriman || it.alamat_toko || "").toLowerCase();
            const delivName = String(it.delivery_name || "").toLowerCase();

            return (
              custName.includes(q) ||
              custCode.includes(q) ||
              smNo.includes(q) ||
              soNo.includes(q) ||
              itemCode.includes(q) ||
              itKet.includes(q) ||
              addr.includes(q) ||
              delivName.includes(q)
            );
          });

          // Pencarian pada properti orders baru (grouped per dokumen)
          const orders = p.orders || [];
          const matchOrders = orders.some((ord) => {
            const custName = String(ord.customer_name || ord.name || "").toLowerCase();
            const custCode = String(ord.customer_code || ord.code || "").toLowerCase();
            const orderNo = String(ord.order_no || ord.no || ord.so_no || "").toLowerCase();
            const refSo = String(ord.ref_so || "").toLowerCase();
            const ordKet = String(ord.keterangan || "").toLowerCase();
            const addr = String(ord.alamat || ord.alamat_pengiriman || ord.alamat_toko || "").toLowerCase();
            const delivName = String(ord.delivery_name || "").toLowerCase();
            const itemsSummary = String(ord.items_summary || "").toLowerCase();
            const matchOrderItems = (ord.items || []).some((it) =>
              String(it.item_code || "").toLowerCase().includes(q)
            );

            return (
              custName.includes(q) ||
              custCode.includes(q) ||
              orderNo.includes(q) ||
              refSo.includes(q) ||
              ordKet.includes(q) ||
              addr.includes(q) ||
              delivName.includes(q) ||
              itemsSummary.includes(q) ||
              matchOrderItems
            );
          });

          return (
            noDp.includes(q) ||
            area.includes(q) ||
            cabang.includes(q) ||
            ket.includes(q) ||
            noPol.includes(q) ||
            sopir.includes(q) ||
            matchItems ||
            matchOrders
          );
        });
      }

      if (state.planFilterArea && state.planFilterArea !== "ALL") {
        list = list.filter(
          (p) =>
            String(p.area || p.antrian_area || "").toUpperCase().trim() ===
            state.planFilterArea.toUpperCase().trim()
        );
      }

      if (state.planFilterCabang && state.planFilterCabang !== "ALL") {
        list = list.filter(
          (p) =>
            String(p.cabang || p.antrian_cabang || "").toUpperCase().trim() ===
            state.planFilterCabang.toUpperCase().trim()
        );
      }

      return list;
    },

    /**
     * Daftar Distribution Plan yang memenuhi syarat untuk dipilih (tidak berstatus bypass diajukan '0' atau disetujui '1')
     */
    selectablePlans: (state) => {
      return (state.filteredDistributionPlans || []).filter((p) => {
        const s = String(p?.bypass_status);
        return s !== "0" && s !== "1";
      });
    },

    isAllPlansSelected: (state) => {
      const list = (state.filteredDistributionPlans || []).filter((p) => {
        const s = String(p?.bypass_status);
        return s !== "0" && s !== "1";
      });
      if (!list || list.length === 0) return false;
      return list.every((p) => state.selectedPlanIds.includes(p.id));
    },

    selectedPlans: (state) => {
      if (!state.selectedPlanIds || state.selectedPlanIds.length === 0) return [];
      const set = new Set(state.selectedPlanIds);
      return state.distributionPlans.filter((p) => set.has(p.id));
    },

    /**
     * Ringkasan Antrean yang dihitung dinamis dan presisi dari availableOrders
     */
    queueSummary: (state) => {
      const orders = state.availableOrders || [];
      const total_orders = orders.length;
      const total_so = orders.filter(
        (o) => String(o.type || "").toUpperCase() === "SO"
      ).length;
      const total_sm = orders.filter(
        (o) => String(o.type || "").toUpperCase() === "SM"
      ).length;
      const total_tonase = orders.reduce(
        (acc, cur) => acc + (Number(cur.total_tonase) || 0),
        0
      );

      return {
        total_orders,
        total_so,
        total_sm,
        total_tonase: Math.round(total_tonase * 1000) / 1000,
      };
    },
  },

  actions: {
    /**
     * Mengambil daftar Distribution Plan yang telah diterbitkan dari Backend API
     */
    async fetchDistributionPlans(force = false) {
      // Jika data sudah termuat di store dan tidak dipaksa reload, gunakan cache instan
      if (!force && this.distributionPlans && this.distributionPlans.length > 0) {
        return;
      }
      if (this.loadingPlans && !force) return;
      this.loadingPlans = true;

      try {
        const params = {};
        if (this.planStartDate) params.start_date = this.planStartDate;
        if (this.planEndDate) params.end_date = this.planEndDate;
        if (this.planFilterCabang && this.planFilterCabang !== "ALL") {
          params.cabang = this.planFilterCabang;
        }
        if (this.planFilterArea && this.planFilterArea !== "ALL") {
          params.area = this.planFilterArea;
        }
        if (this.planSearchQuery) {
          params.search = this.planSearchQuery;
        }

        const response = await axios.get(
          `${API_BASE_URL}/distribution-plans`,
          {
            headers: authHeader(),
            params,
          }
        );

        const serverData = response.data?.data || [];
        const normalizedData = serverData.map((dp) => {
          let items = Array.isArray(dp.items)
            ? dp.items
            : Array.isArray(dp.detail)
              ? dp.detail
              : [];

          // Defensive fallback: jika items kosong, buat 1 item dari shortcut properti baris pertama
          if (items.length === 0 && (dp.dtl_nama || dp.dtl_ledger)) {
            items = [
              {
                id: dp.id,
                type: dp.type || "SO",
                cabang: dp.cabang || dp.antrian_cabang,
                area: dp.area || dp.antrian_area,
                tanggal: dp.tgl_kirim || dp.tanggal,
                no: dp.no || dp.dtl_sm || "-",
                so_no: dp.so_no || dp.dtl_so || "-",
                code: dp.dtl_ledger,
                item_code: dp.item_code || "-",
                item_qty: dp.item_qty || dp.total_muatan || 0,
                item_ton: Number(dp.total_muatan || 0),
                keterangan: dp.dtl_keterangan || dp.catatan || "-",
                name: dp.dtl_nama,
                alamat_toko: dp.dtl_alamat,
                alamat_pengiriman: dp.alamat_pengiriman || "",
                delivery_name: dp.delivery_name || "",
                latitude: dp.latitude || dp.lat,
                longitude: dp.longitude || dp.lng,
                lat: dp.lat || dp.latitude,
                lng: dp.lng || dp.longitude,
                delivery_address: dp.delivery_address || null,
              },
            ];
          }

          return {
            ...dp,
            items,
            detail: items,
            total_muatan: Number(dp.total_muatan || 0),
          };
        });

        const localOnly = this.recentCreatedPlans.filter(
          (lp) => !normalizedData.some((sp) => sp.no_dp === lp.no_dp)
        );
        this.distributionPlans = [...localOnly, ...normalizedData];
      } catch (err) {
        console.warn("Gagal memuat daftar Distribution Plan dari API, fallback ke session lokal:", err);
        this.distributionPlans = [...this.recentCreatedPlans];
      } finally {
        this.loadingPlans = false;
      }
    },

    /**
     * Reset filter pencarian dan parameter query Displan
     */
    resetPlanFilters() {
      this.planStartDate = "";
      this.planEndDate = "";
      this.planFilterCabang = "";
      this.planFilterArea = "";
      this.planSearchQuery = "";
      return this.fetchDistributionPlans(true);
    },

    /**
     * Toggle centang satu dokumen Distribution Plan
     */
    toggleSelectPlan(planId) {
      if (!planId) return;
      const plan = (this.distributionPlans || []).find((p) => p.id === planId);
      if (plan) {
        const s = String(plan.bypass_status);
        if (s === "0" || s === "1") {
          return; // Jangan izinkan dipilih jika bypass sedang diajukan atau sudah disetujui
        }
      }
      const idx = this.selectedPlanIds.indexOf(planId);
      if (idx > -1) {
        this.selectedPlanIds.splice(idx, 1);
      } else {
        this.selectedPlanIds.push(planId);
      }
    },

    /**
     * Centang / Hapus centang semua dokumen Distribution Plan yang tampil di layar
     */
    toggleSelectAllPlans() {
      const list = this.selectablePlans;
      if (this.isAllPlansSelected) {
        const selectableIds = new Set(list.map((p) => p.id));
        this.selectedPlanIds = this.selectedPlanIds.filter((id) => !selectableIds.has(id));
      } else {
        const newSet = new Set(this.selectedPlanIds);
        list.forEach((p) => {
          if (p.id) newSet.add(p.id);
        });
        this.selectedPlanIds = Array.from(newSet);
      }
    },

    /**
     * Bersihkan seleksi centang dokumen Distribution Plan
     */
    clearPlanSelection() {
      this.selectedPlanIds = [];
    },

    /**
     * 1. Mengambil daftar antrean order siap DP dari Backend API
     */
    async fetchAvailableOrders() {
      if (this.loading) return;
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(
          `${API_BASE_URL}/distribution-plans/available-orders`,
          {
            headers: authHeader(),
            params: {
              type: "ALL",
            },
          }
        );

        if (response.data && response.data.status === "success") {
          const rawList = response.data.data || [];
          this.availableOrders = rawList.map((o, idx) => {
            const docNum = o.so_no || o.sm_no || o.no || `order_${idx}`;
            const uniqueKey =
              o.key ||
              `${o.type || "DOC"}_${docNum}_${o.code_comp || ""}_${idx}`;
            return {
              ...o,
              key: uniqueKey,
            };
          });

          this.summary = {
            total_orders: this.availableOrders.length,
            total_so: this.availableOrders.filter(
              (o) => String(o.type).toUpperCase() === "SO"
            ).length,
            total_sm: this.availableOrders.filter(
              (o) => String(o.type).toUpperCase() === "SM"
            ).length,
            total_tonase:
              Math.round(
                this.availableOrders.reduce(
                  (acc, cur) => acc + (Number(cur.total_tonase) || 0),
                  0
                ) * 1000
              ) / 1000,
          };
          this.lastFetched = new Date();
        } else {
          const rawList = response.data?.data || [];
          this.availableOrders = rawList.map((o, idx) => {
            const docNum = o.so_no || o.sm_no || o.no || `order_${idx}`;
            return {
              ...o,
              key:
                o.key ||
                `${o.type || "DOC"}_${docNum}_${o.code_comp || ""}_${idx}`,
            };
          });

          this.summary = {
            total_orders: this.availableOrders.length,
            total_so: this.availableOrders.filter(
              (o) => String(o.type).toUpperCase() === "SO"
            ).length,
            total_sm: this.availableOrders.filter(
              (o) => String(o.type).toUpperCase() === "SM"
            ).length,
            total_tonase:
              Math.round(
                this.availableOrders.reduce(
                  (acc, cur) => acc + (Number(cur.total_tonase) || 0),
                  0
                ) * 1000
              ) / 1000,
          };
          this.lastFetched = new Date();
        }
      } catch (err) {
        console.error("Gagal memuat antrean Distribution Plan:", err);
        this.error =
          err.response?.data?.message ||
          "Gagal memuat daftar pesanan siap distribusi dari server.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * 2. Toggle seleksi satu dokumen order
     */
    toggleSelectOrder(docKey) {
      if (!docKey) return;
      const idx = this.selectedKeys.indexOf(docKey);
      if (idx > -1) {
        this.selectedKeys.splice(idx, 1);
      } else {
        this.selectedKeys.push(docKey);
      }
    },

    /**
     * 3. Select / Deselect Semua dokumen yang sedang tampil di layar
     */
    toggleSelectAllFiltered() {
      const list = this.filteredOrders;
      if (this.isAllSelected) {
        // Deselect all filtered items
        const filteredKeys = new Set(list.map((o) => o.key));
        this.selectedKeys = this.selectedKeys.filter(
          (k) => !filteredKeys.has(k)
        );
      } else {
        // Select all filtered items
        const newSet = new Set(this.selectedKeys);
        list.forEach((o) => newSet.add(o.key));
        this.selectedKeys = Array.from(newSet);
      }
    },

    /**
     * 4. Bersihkan seluruh centang
     */
    clearSelection() {
      this.selectedKeys = [];
      this.modalForm.keterangan = "";
      this.modalForm.no_dp_gandengan = "";
    },

    /**
     * 5. Membuka Modal "Distribution Plan Gabungan"
     * Menyiapkan form default & rincian item barang yang bisa diedit Qty-nya
     */
    openCreateModal() {
      const selected = this.selectedOrders;
      if (!selected || selected.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Pilih Pesanan",
          text: "Silakan centang minimal satu pesanan (SO / SM) untuk dibuatkan Distribution Plan.",
          confirmButtonColor: "#405189",
        });
        return;
      }

      // Validasi keseragaman cabang/area pelaksana antar order terpilih
      const distinctBranches = new Set();
      selected.forEach((o) => {
        const b = (o.wh_office || o.area || o.kp_code || o.cabang || "").trim().toUpperCase();
        if (b) distinctBranches.add(b);
      });
      if (distinctBranches.size > 1) {
        Swal.fire({
          icon: "warning",
          title: "Cabang Pelaksana Berbeda",
          html: `Pesanan yang dipilih berasal dari cabang pelaksana yang berbeda: <b>${Array.from(distinctBranches).join(", ")}</b>.<br/><br/>Satu Distribution Plan hanya dapat dibuat untuk pesanan dengan cabang pelaksana yang sama.`,
          confirmButtonColor: "#405189",
        });
        return;
      }

      // Ambil cabang/area pelaksana default dari order yang pertama kali dipilih
      const firstOrder = selected[0];
      const defaultWhOffice =
        firstOrder.wh_office ||
        firstOrder.area ||
        firstOrder.kp_code ||
        firstOrder.cabang ||
        "";
      const defaultArea = defaultWhOffice;

      // Siapkan baris item yang bisa diedit Qty-nya
      const itemsPayload = [];
      selected.forEach((order) => {
        const orderItems =
          order.items && order.items.length > 0
            ? order.items
            : [
              {
                item_code: order.item_code || "ITEM",
                item_name: order.items_summary || "Item Pesanan",
                sell_uom: order.sell_uom || "Unit",
                item_qty: order.total_qty || 1,
                item_qty2: order.total_qty || 1,
                item_weight: 0,
                item_ton: order.total_tonase || 0,
              },
            ];

        orderItems.forEach((item, itemIdx) => {
          const maxVal = Number(
            item.item_qty2 > 0
              ? item.item_qty2
              : item.item_qty > 0
                ? item.item_qty
                : 1
          );

          itemsPayload.push({
            uniqueId: `${order.key}_${item.item_code}_${itemIdx}`,
            docKey: order.key,
            type: order.type,
            no: order.no || order.so_no || order.sm_no,
            so_no: order.so_no || order.no,
            sm_no: order.sm_no || "",
            sales_form_no: order.sales_form_no || "",
            order_date: order.tanggal,
            order_tanggal: order.tanggal,
            jaminput: order.jaminput || "",
            code_comp: order.code_comp,
            cabang: order.cabang,
            kp_code: order.kp_code,
            freight_type: order.freight_type || "kirim",
            ledger: order.code,
            code: order.code,
            name: order.ledgername,
            ledgername: order.ledgername,
            alamat_toko: order.alamat_toko || "",
            alamat_pengiriman:
              order.alamat_pengiriman || order.delivery_address || "",
            delivery_address:
              order.delivery_address || order.alamat_pengiriman || "",
            delivery_name: order.delivery_name || "",
            address_code: order.address_code || "",
            latitude: order.latitude || order.lat || null,
            longitude: order.longitude || order.lng || null,
            order_keterangan: order.keterangan || "",
            sales_code: order.sales_code || "",
            dept_code: order.dept_code || "",
            wh_office: order.wh_office || defaultWhOffice,
            pr_stat: order.pr_stat || "A",
            delivery_time: order.delivery_time || null,
            delivery_date: order.delivery_date || null,
            tgl_permintaan_kirim: order.tgl_permintaan_kirim || null,
            term: order.term || null,
            print_soa: order.print_soa || null,
            idno1: order.idno1 || null,
            nopol: order.nopol || null,
            sopir: order.sopir || null,
            hpsopir: order.hpsopir || null,
            print_sq: order.print_sq || 0,
            desa: order.desa || "",
            jarak_km: order.jarak_km || 0,
            ds_flag: item.ds_flag || order.ds_flag || "",
            item_code: item.item_code,
            item_name: item.item_name,
            sell_uom: item.sell_uom || "Unit",
            item_qty: Number(item.item_qty || maxVal),
            max_qty: maxVal,
            qty: maxVal, // Default muat penuh sisa kuota
            item_weight: Number(item.item_weight || 0),
            item_ton: Number(item.item_ton || 0),
            tipe_muat: item.tipe_muat || order.tipe_muat || "Langsung",
            item_curah: Boolean(item.item_curah),
            dedikasi: item.dedikasi || order.dedikasi || "",
            raw_order: order,
          });
        });
      });

      // Sesuai permintaan user: saat modal dibuka, selalu kosongkan keterangan dan no dp gandengan agar dipilih ulang
      this.modalForm = {
        tanggal: new Date().toLocaleDateString("en-CA"),
        area: defaultArea,
        wh_office: defaultWhOffice,
        keterangan: "",
        no_dp_gandengan: "",
        ambil: false, // Disembunyikan (belum digunakan)
        full_load: true, // Wajib Full Load jika DP Mandiri (bukan gandengan)
        prioritas: false,
        truk_luar: false, // Tidak wajib dipilih
        truk_sna: false, // Tidak wajib dipilih
        perlu_gandengan: false, // Khusus Cabang PRJ
        items: itemsPayload,
      };

      this.isModalOpen = true;
    },

    /**
     * 6. Menutup modal form DP
     */
    closeCreateModal() {
      this.isModalOpen = false;
      this.modalForm.keterangan = "";
      this.modalForm.no_dp_gandengan = "";
    },

    /**
     * 7. Eksekusi Simpan Distribution Plan (POST ke server)
     */
    async submitDisplan() {
      if (this.isSubmitting) return;

      // Validasi tanggal tidak boleh tanggal lampau
      const todayStr = new Date().toLocaleDateString("en-CA");
      if (this.modalForm.tanggal && this.modalForm.tanggal < todayStr) {
        Swal.fire({
          icon: "warning",
          title: "Tanggal Tidak Valid",
          text: "Tanggal perintah kirim tidak boleh tanggal yang telah berlalu.",
          confirmButtonColor: "#405189",
        });
        return;
      }

      // Validasi form
      if (!this.modalForm.keterangan || !this.modalForm.keterangan.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Keterangan Wajib Diisi",
          text: "Silakan masukkan keterangan atau catatan distribusi sebelum menyimpan.",
          confirmButtonColor: "#405189",
        });
        return;
      }

      // Validasi: Full Load wajib jika bukan gandengan
      const isGandengan = Boolean(
        this.modalForm.no_dp_gandengan &&
        String(this.modalForm.no_dp_gandengan).trim().length > 0
      );
      if (!isGandengan && !this.modalForm.full_load) {
        Swal.fire({
          icon: "warning",
          title: "Wajib Full Load",
          text: "Distribution Plan Mandiri (bukan gandengan) wajib berstatus Full Load.",
          confirmButtonColor: "#405189",
        });
        return;
      }

      // Validasi Qty item tidak boleh <= 0 atau melebihi max_qty
      for (const it of this.modalForm.items) {
        if (!it.qty || Number(it.qty) <= 0) {
          Swal.fire({
            icon: "warning",
            title: "Qty Tidak Valid",
            text: `Qty untuk item ${it.item_code} (${it.name}) harus lebih dari 0.`,
            confirmButtonColor: "#405189",
          });
          return;
        }
        if (Number(it.qty) > Number(it.max_qty)) {
          Swal.fire({
            icon: "warning",
            title: "Qty Melebihi Kuota",
            text: `Qty untuk item ${it.item_code} tidak boleh melebihi sisa pesanan (${it.max_qty} ${it.sell_uom}).`,
            confirmButtonColor: "#405189",
          });
          return;
        }
      }

      this.isSubmitting = true;

      // Ambil cabang/area pelaksana distribusi
      const pelaksana = this.modalForm.wh_office || this.modalForm.area || "";

      // Hitung total akumulasi & lengkapi data orders/items
      let grandTotalQty = 0;
      let grandTotalTonase = 0;
      let hasCurah = false;

      const mappedOrders = this.modalForm.items.map((it) => {
        const qtyVal = Number(it.qty) || 0;
        grandTotalQty += qtyVal;

        // Hitung tonase secara proporsional sesuai kuantitas yang dimuat
        let calculatedTon = 0;
        if (Number(it.item_weight) > 0) {
          calculatedTon =
            Math.round(((qtyVal * Number(it.item_weight)) / 1000000) * 10000) /
            10000;
        } else if (Number(it.max_qty) > 0 && Number(it.item_ton) > 0) {
          calculatedTon =
            Math.round(
              ((qtyVal / Number(it.max_qty)) * Number(it.item_ton)) * 10000
            ) / 10000;
        } else {
          calculatedTon = Number(it.item_ton) || 0;
        }
        grandTotalTonase += calculatedTon;

        if (it.item_curah) {
          hasCurah = true;
        }

        return {
          type: it.type,
          no: it.no || it.so_no || it.sm_no,
          so_no: it.so_no,
          sm_no: it.sm_no || null,
          sales_form_no: it.sales_form_no || null,
          tanggal: it.order_tanggal || it.order_date || this.modalForm.tanggal,
          jaminput: it.jaminput || null,
          code_comp: it.code_comp,
          cabang: it.cabang || this.modalForm.area,
          kp_code: it.kp_code || null,
          area: it.cabang || this.modalForm.area,
          freight_type: it.freight_type || "kirim",
          code: it.code || it.ledger,
          ledger: it.ledger || it.code,
          name: it.name || it.ledgername,
          ledgername: it.ledgername || it.name,
          alamat_toko: it.alamat_toko || "",
          alamat_pengiriman:
            it.alamat_pengiriman || it.delivery_address || "",
          delivery_address:
            it.delivery_address || it.alamat_pengiriman || "",
          delivery_name: it.delivery_name || "",
          address_code: it.address_code || "",
          latitude: it.latitude || it.lat || null,
          longitude: it.longitude || it.lng || null,
          keterangan: it.order_keterangan || "",
          sales_code: it.sales_code || "",
          dept_code: it.dept_code || "",
          wh_office: it.wh_office || pelaksana,
          pr_stat: it.pr_stat || "A",
          delivery_time: it.delivery_time || null,
          delivery_date: it.delivery_date || null,
          tgl_permintaan_kirim: it.tgl_permintaan_kirim || null,
          term: it.term || null,
          nopol: it.nopol || null,
          sopir: it.sopir || null,
          hpsopir: it.hpsopir || null,
          print_sq: it.print_sq || 0,
          print_soa: it.print_soa || null,
          idno1: it.idno1 || null,
          desa: it.desa || "",
          jarak_km: it.jarak_km || 0,
          prioritas: this.modalForm.prioritas ? "Y" : "N",
          ds_flag: it.ds_flag || "",
          item_code: it.item_code,
          item_name: it.item_name,
          sell_uom: it.sell_uom,
          item_qty: Number(it.item_qty || it.max_qty),
          item_qty2: qtyVal,
          qty: qtyVal,
          item_weight: Number(it.item_weight || 0),
          item_ton: calculatedTon,
          tipe_muat:
            it.tipe_muat ||
            (qtyVal < Number(it.item_qty) ? "Outstanding" : "Langsung"),
          item_curah: it.item_curah ? "Y" : "N",
          dedikasi: it.dedikasi || "",
        };
      });

      // Cek status dedikasi pelanggan dari item pesanan
      let hasDedikasi = false;
      this.modalForm.items.forEach((it) => {
        if (
          it.dedikasi === "Y" ||
          it.dedikasi === true ||
          String(it.dedikasi).toUpperCase() === "Y"
        ) {
          hasDedikasi = true;
        }
      });

      // Susun payload lengkap sesuai tabel t_dp_antrian & t_dp_detail
      const payload = {
        tanggal: this.modalForm.tanggal,
        cabang: pelaksana,
        area: pelaksana,
        wh_office: pelaksana,
        keterangan: this.modalForm.keterangan.trim(),
        alasan: this.modalForm.keterangan.trim(),
        no_dp_gandengan: this.modalForm.no_dp_gandengan
          ? this.modalForm.no_dp_gandengan.trim()
          : null,
        perlu_gandengan: (isGandengan || this.modalForm.perlu_gandengan) ? "Y" : "N",
        ambil: this.modalForm.ambil ? "Y" : "N",
        full_load: this.modalForm.full_load ? "Y" : "N",
        prioritas: this.modalForm.prioritas ? "Y" : "N",
        truk_luar: this.modalForm.truk_luar ? "Y" : "N",
        truk_sna: this.modalForm.truk_sna ? "Y" : "N",
        semen_curah: hasCurah ? "Y" : "N",
        dedikasi: hasDedikasi ? "Y" : null,
        total_orders: this.selectedKeys.length || 1,
        total_items: mappedOrders.length,
        total_qty: Math.round(grandTotalQty * 100) / 100,
        total_tonase: Math.round(grandTotalTonase * 1000) / 1000,
        orders: mappedOrders,
      };

      try {
        Swal.fire({
          title: "Menyimpan Distribution Plan...",
          text: "Sedang memproses antrean pengiriman barang. Mohon menunggu sekitar 30-60 detik",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const res = await axios.post(
          `${API_BASE_URL}/distribution-plans`,
          payload,
          {
            headers: authHeader(),
          }
        );

        Swal.close();

        // Validasi respon jika backend mengembalikan status error dalam payload meta
        if (res.data?.meta && res.data.meta.code >= 400) {
          throw new Error(res.data.meta.message || "Gagal menyimpan Distribution Plan");
        }

        // Rekam ke antrean lokal agar langsung tampil di daftar Displan
        const generatedNoDp =
          res.data?.data?.no_dp ||
          `DP/${payload.area}/${new Date().getFullYear().toString().slice(-2)}/${Math.floor(
            10000 + Math.random() * 90000
          )}`;

        const createdRecord = {
          id: res.data?.data?.dp_id || res.data?.data?.id || generatedNoDp,
          no_dp: generatedNoDp,
          no_antrian: res.data?.data?.no_antrian || null,
          tanggal: payload.tanggal,
          tgl_kirim: payload.tanggal,
          area: payload.area,
          cabang: payload.cabang,
          antrian_area: payload.area,
          antrian_cabang: payload.cabang,
          keterangan: payload.keterangan,
          no_dp_gandengan: payload.no_dp_gandengan || null,
          ambil: payload.ambil === "Y",
          full_load: payload.full_load === "Y",
          prioritas: payload.prioritas === "Y",
          truk_luar: payload.truk_luar === "Y",
          truk_sna: payload.truk_sna === "Y",
          perlu_gandengan: payload.perlu_gandengan === "Y",
          dedikasi: payload.dedikasi,
          total_items: payload.orders.length,
          total_muatan: payload.total_tonase || 0,
          total_tonase: payload.total_tonase || 0,
          items: [...this.modalForm.items],
          detail: [...this.modalForm.items],
          status: "Antre",
          created_at: new Date().toLocaleTimeString(),
        };

        this.recentCreatedPlans.unshift(createdRecord);
        this.distributionPlans.unshift(createdRecord);

        await Swal.fire({
          icon: "success",
          title: "Berhasil Dibuat!",
          html: `Distribution Plan berhasil didaftarkan ke sistem dengan nomor: <strong class="text-primary font-monospace fs-15">${generatedNoDp}</strong>`,
          confirmButtonColor: "#0ab39c",
        });

        // Bersihkan seleksi dan tutup modal
        this.clearSelection();
        this.closeCreateModal();

        // Segarkan data antrean & daftar dokumen Distribution Plan dari backend secara real-time
        this.fetchAvailableOrders(true);
        this.fetchDistributionPlans(true);

        return { success: true, no_dp: generatedNoDp };
      } catch (err) {
        Swal.close();
        console.error("Gagal simpan Distribution Plan:", err);

        const errorMessage =
          err.response?.data?.meta?.message ||
          err.response?.data?.message ||
          err.message ||
          "Terjadi kesalahan saat menyimpan Distribution Plan.";

        const statusCode =
          err.response?.status ||
          err.response?.data?.meta?.code ||
          "Error";

        await Swal.fire({
          icon: "error",
          title: "Gagal Menyimpan Displan",
          html: `
            <div class="text-danger fw-semibold mb-2 fs-13 text-start bg-danger-subtle p-3 rounded border border-danger-subtle font-monospace" style="word-break: break-word; white-space: pre-wrap;">
              ${errorMessage}
            </div>
            <div class="text-muted fs-11 text-start">
              Kode Status Backend: <strong>${statusCode}</strong>
            </div>
          `,
          confirmButtonColor: "#f06548",
        });

        return { success: false, message: errorMessage };
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * 10. Mengajukan bypass antrean untuk Distribution Plan terpilih
     * Payload backend: { selectedData: ["DP/..."], notes: "..." }
     *
     * @param {Object|Array} payloadOrPlanIds - Object { selectedData, notes } atau Array No DP
     * @param {string} [reasonFallback] - Alasan fallback jika argumen pertama berupa array
     * @returns {Promise<Object>}
     */
    async submitBypassRequest(payloadOrPlanIds, reasonFallback = "") {
      let selectedData = [];
      let notes = "";

      if (Array.isArray(payloadOrPlanIds)) {
        selectedData = payloadOrPlanIds;
        notes = reasonFallback;
      } else if (typeof payloadOrPlanIds === "object" && payloadOrPlanIds !== null) {
        selectedData = payloadOrPlanIds.selectedData || payloadOrPlanIds.plan_ids || [];
        notes = payloadOrPlanIds.notes || payloadOrPlanIds.reason || "";
      }

      // Pastikan data yang dikirim adalah nomor DP (string "DP/..."), bukan ID numerik
      selectedData = selectedData.map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.no_dp || item.id;
        }
        // Jika yang diteruskan adalah ID numerik, cari no_dp di distributionPlans
        if (typeof item === "number" || (/^\d+$/).test(String(item))) {
          const matchPlan = this.distributionPlans.find((p) => p.id === Number(item) || String(p.id) === String(item));
          return matchPlan ? matchPlan.no_dp : item;
        }
        return item;
      }).filter(Boolean);

      if (!selectedData || selectedData.length === 0) {
        throw new Error("Pilih setidaknya satu nomor DP untuk diajukan bypass antrian.");
      }

      if (!notes || !notes.trim()) {
        throw new Error("Keterangan/alasan pengajuan bypass antrian wajib diisi.");
      }

      try {
        const payload = {
          selectedData,
          notes: notes.trim(),
        };

        const response = await axios.post(
          `${API_BASE_URL}/distribution-plans/bypass`,
          payload,
          {
            headers: authHeader(),
          }
        );

        if (response.data?.meta && response.data.meta.code >= 400) {
          throw new Error(response.data.meta.message || "Gagal mengajukan bypass antrian DP.");
        }

        return {
          success: true,
          data: response.data?.data || response.data,
          message: response.data?.message || "Pengajuan bypass Distribution Plan berhasil diajukan.",
        };
      } catch (err) {
        console.error("Gagal mengajukan bypass antrean DP:", err);
        const errorMessage =
          err.response?.data?.meta?.message ||
          err.response?.data?.message ||
          err.message ||
          "Terjadi kesalahan saat mengajukan bypass antrean Distribution Plan.";
        throw new Error(errorMessage);
      }
    },

    /**
     * 11. Ambil detail data pengajuan bypass untuk verifikasi/approval
     *
     * @param {Object} params - { token, requestId }
     * @returns {Promise<Object>}
     */
    async fetchBypassDetail({ token, requestId }) {
      const qToken = token ? encodeURIComponent(token) : "";
      const qId = requestId ? encodeURIComponent(requestId) : "";

      let endpoint = `${API_BASE_URL}/distribution-plans/bypass/detail`;
      let isPublic = false;

      // Jika ada token email, utamakan endpoint public tanpa header auth wajib
      if (token) {
        endpoint = `${API_BASE_URL}/distribution-plans/public/bypass/detail?token=${qToken}`;
        isPublic = true;
      } else {
        endpoint = `${API_BASE_URL}/distribution-plans/bypass/detail?request_id=${qId}`;
      }

      try {
        const config = isPublic ? {} : { headers: authHeader() };
        const response = await axios.get(endpoint, config);

        if (response.data?.meta && response.data.meta.code >= 400) {
          throw new Error(response.data.meta.message || "Gagal memuat detail pengajuan bypass.");
        }

        return response.data?.data || response.data;
      } catch (err) {
        // Fallback jika endpoint public/auth butuh URL alternatif
        if (isPublic && err.response?.status === 404) {
          try {
            const fallbackRes = await axios.get(
              `${API_BASE_URL}/distribution-plans/bypass/detail?token=${qToken}`,
              { headers: authHeader() }
            );
            return fallbackRes.data?.data || fallbackRes.data;
          } catch (innerErr) {
            console.error("Fallback bypass detail juga gagal:", innerErr);
          }
        }

        console.error("Gagal memuat detail pengajuan bypass DP:", err);
        const errorMessage =
          err.response?.data?.meta?.message ||
          err.response?.data?.message ||
          err.message ||
          "Gagal memuat detail data permohonan bypass antrean.";
        throw new Error(errorMessage);
      }
    },

    /**
     * 12. Eksekusi Verifikasi / Approval Pengajuan Bypass DP
     *
     * @param {Object} payload - { token, requestId, status: '1'|'2', notes: string }
     * @returns {Promise<Object>}
     */
    async verifyBypassRequest({ token, requestId, status, notes }) {
      const body = {
        token: token || undefined,
        request_id: requestId || undefined,
        status: String(status), // '1' = Setuju, '2' = Tolak
        notes: (notes || "").trim(),
      };

      let endpoint = `${API_BASE_URL}/distribution-plans/bypass/verify`;
      let isPublic = false;

      if (token) {
        endpoint = `${API_BASE_URL}/distribution-plans/public/bypass/verify`;
        isPublic = true;
      }

      try {
        const config = isPublic ? {} : { headers: authHeader() };
        const response = await axios.post(endpoint, body, config);

        if (response.data?.meta && response.data.meta.code >= 400) {
          throw new Error(response.data.meta.message || "Gagal memproses verifikasi bypass.");
        }

        return {
          success: true,
          data: response.data?.data || response.data,
          message: response.data?.message || "Verifikasi bypass antrian berhasil diproses.",
        };
      } catch (err) {
        // Fallback coba endpoint auth jika public gagal
        if (isPublic && err.response?.status === 404) {
          try {
            const fallbackRes = await axios.post(
              `${API_BASE_URL}/distribution-plans/bypass/verify`,
              body,
              { headers: authHeader() }
            );
            return {
              success: true,
              data: fallbackRes.data?.data || fallbackRes.data,
              message: fallbackRes.data?.message || "Verifikasi bypass antrian berhasil diproses.",
            };
          } catch (innerErr) {
            console.error("Fallback verify bypass juga gagal:", innerErr);
          }
        }

        console.error("Gagal memproses verifikasi bypass DP:", err);
        const errorMessage =
          err.response?.data?.meta?.message ||
          err.response?.data?.message ||
          err.message ||
          "Terjadi kesalahan saat memproses verifikasi permohonan bypass.";
        throw new Error(errorMessage);
      }
    },

    /**
     * Eksekusi Simpan DP Gandengan Cepat langsung (dari Drawer di Halaman List)
     * @param {Object} data - { masterPlan, tanggal, area, keterangan, full_load, prioritas, truk_luar, truk_sna, selectedOrders }
     */
    async submitQuickGandengan(data) {
      if (this.isSubmitting) return { success: false };
      this.isSubmitting = true;

      const masterNoDp = data.masterPlan?.no_dp || "";
      const orders = data.selectedOrders || [];

      // Validasi minimal 1 order
      if (orders.length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Belum Ada Order Dipilih",
          text: "Silakan centang minimal satu pesanan untuk muatan gandengan.",
          confirmButtonColor: "#405189",
        });
        this.isSubmitting = false;
        return { success: false };
      }

      // Validasi keterangan
      if (!data.keterangan || data.keterangan.trim().length === 0) {
        Swal.fire({
          icon: "warning",
          title: "Keterangan Wajib Diisi",
          text: "Mohon isi catatan atau keterangan rute untuk pengiriman gandengan ini.",
          confirmButtonColor: "#405189",
        });
        this.isSubmitting = false;
        return { success: false };
      }

      // Format items & orders
      let grandTotalQty = 0;
      let grandTotalTonase = 0;
      let hasCurah = false;

      const mappedOrders = [];
      const recordedItems = [];

      orders.forEach((o) => {
        const orderItems = o.items || [
          {
            item_code: o.item_code,
            item_name: o.item_name,
            qty: o.total_qty || o.qty || 1,
            item_qty: o.total_qty || o.qty || 1,
            item_weight: o.item_weight || 0,
            item_ton: o.total_tonase || o.item_ton || 0,
            sell_uom: o.sell_uom || "Unit",
            item_curah: Boolean(o.item_curah),
          },
        ];

        orderItems.forEach((item) => {
          const qtyVal = Number(item.qty || item.item_qty || 0);
          const weightVal = Number(item.item_weight || 0);
          const calculatedTon =
            item.item_ton && item.item_qty
              ? Number(((item.item_ton / item.item_qty) * qtyVal).toFixed(3))
              : Number(((weightVal * qtyVal) / 1000).toFixed(3));

          grandTotalQty += qtyVal;
          grandTotalTonase += calculatedTon;
          if (item.item_curah) hasCurah = true;

          const itemObj = {
            key: item.key || `${o.type || "DOC"}_${o.so_no || o.sm_no || o.no || ""}_${item.item_code}`,
            doc_no: o.so_no || o.sm_no || o.no,
            so_no: o.so_no || (o.type === "SO" ? o.no : null),
            sm_no: o.sm_no || (o.type === "SM" ? o.no : null),
            type: o.type || "SO",
            code_comp: o.code_comp,
            customer_code: o.customer_code || o.ledger,
            customer_name: o.customer_name || o.name,
            item_code: item.item_code,
            item_name: item.item_name,
            sell_uom: item.sell_uom || "Unit",
            qty: qtyVal,
            item_qty: qtyVal,
            item_weight: weightVal,
            item_ton: calculatedTon,
            tipe_muat: "Langsung",
            item_curah: item.item_curah ? "Y" : "N",
            dedikasi: item.dedikasi || o.dedikasi || "",
            alamat_pengiriman: o.alamat_pengiriman || o.delivery_address,
            delivery_name: o.delivery_name,
            keterangan: o.keterangan || data.keterangan,
          };
          mappedOrders.push(itemObj);
          recordedItems.push(itemObj);
        });
      });

      const isDedikasiGandengan = Boolean(
        data.masterPlan?.dedikasi === "Y" ||
        data.masterPlan?.dedikasi === true ||
        orders.some((o) => o.dedikasi === "Y" || o.dedikasi === true)
      );

      const payload = {
        tanggal: data.tanggal,
        cabang: data.area,
        area: data.area,
        keterangan: data.keterangan.trim(),
        alasan: data.keterangan.trim(),
        no_dp_gandengan: masterNoDp,
        perlu_gandengan: "Y",
        ambil: "N",
        full_load: data.full_load ? "Y" : "N",
        prioritas: data.prioritas ? "Y" : "N",
        truk_luar: data.truk_luar ? "Y" : "N",
        truk_sna: data.truk_sna ? "Y" : "N",
        semen_curah: hasCurah ? "Y" : "N",
        dedikasi: isDedikasiGandengan ? "Y" : null,
        total_orders: orders.length,
        total_items: mappedOrders.length,
        total_qty: Math.round(grandTotalQty * 100) / 100,
        total_tonase: Math.round(grandTotalTonase * 1000) / 1000,
        orders: mappedOrders,
      };

      try {
        Swal.fire({
          title: "Menyimpan DP Gandengan...",
          text: `Sedang menautkan gandengan ke ${masterNoDp}`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const res = await axios.post(
          `${API_BASE_URL}/distribution-plans`,
          payload,
          {
            headers: authHeader(),
          }
        );

        Swal.close();

        // Validasi respon jika backend mengembalikan status error dalam payload meta
        if (res.data?.meta && res.data.meta.code >= 400) {
          throw new Error(res.data.meta.message || "Gagal menyimpan Distribution Plan Gandengan");
        }

        const generatedNoDp =
          res.data?.data?.no_dp ||
          `DP/${payload.area}/${new Date().getFullYear().toString().slice(-2)}/${Math.floor(
            10000 + Math.random() * 90000
          )}`;

        const createdRecord = {
          no_dp: generatedNoDp,
          tanggal: payload.tanggal,
          area: payload.area,
          keterangan: payload.keterangan,
          no_dp_gandengan: masterNoDp,
          perlu_gandengan: true,
          ambil: false,
          full_load: payload.full_load === "Y",
          prioritas: payload.prioritas === "Y",
          truk_luar: payload.truk_luar === "Y",
          truk_sna: payload.truk_sna === "Y",
          dedikasi: payload.dedikasi,
          total_items: mappedOrders.length,
          total_muatan: Math.round(grandTotalTonase * 1000) / 1000,
          items: recordedItems,
          status: "Antre",
          created_at: new Date().toLocaleTimeString(),
        };

        this.recentCreatedPlans.unshift(createdRecord);
        this.distributionPlans.unshift(createdRecord);

        await Swal.fire({
          icon: "success",
          title: "DP Gandengan Berhasil Dibuat!",
          html: `Tergandeng ke DP Induk: <strong class="text-secondary font-monospace">${masterNoDp}</strong><br/>Nomor DP Gandengan Baru: <strong class="text-primary font-monospace fs-15">${generatedNoDp}</strong>`,
          confirmButtonColor: "#0ab39c",
        });

        this.fetchAvailableOrders(true);
        this.fetchDistributionPlans(true);
        return { success: true, no_dp: generatedNoDp };
      } catch (err) {
        Swal.close();
        console.error("Gagal simpan DP Gandengan via API:", err);

        const errorMessage =
          err.response?.data?.meta?.message ||
          err.response?.data?.message ||
          err.message ||
          "Terjadi kesalahan saat menyimpan DP Gandengan.";

        const statusCode =
          err.response?.status ||
          err.response?.data?.meta?.code ||
          "Error";

        await Swal.fire({
          icon: "error",
          title: "Gagal Menyimpan DP Gandengan",
          html: `
            <div class="text-danger fw-semibold mb-2 fs-13 text-start bg-danger-subtle p-3 rounded border border-danger-subtle font-monospace" style="word-break: break-word; white-space: pre-wrap;">
              ${errorMessage}
            </div>
            <div class="text-muted fs-11 text-start">
              Kode Status Backend: <strong>${statusCode}</strong>
            </div>
          `,
          confirmButtonColor: "#f06548",
        });

        return { success: false, message: errorMessage };
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
